import React, { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import styles from './RelatedProducts.module.css';

const MAX_FEATURES = 4;

const RelatedProducts = ({
  title = 'Explore Our Premium Range',
  subtitle = 'Discover our next-generation certified food-grade, greaseproof, and 100% compostable paper solutions.',
  products = [
    {
      id: 1,
      name: 'PE Coated Rolls',
      category: 'Industrial Rolls',
      image: '/images/products/pe-rolls.png',
      spec: '35 - 45 GSM',
      features: ['Moisture Barrier', 'Gas Proof', 'Industrial Grade', 'Custom Widths'],
      link: '/products/pe-coated-rolls',
    },
    {
      id: 2,
      name: 'Notebook Cover Jumbo Rolls',
      category: 'Educational',
      image: '/images/products/Notebook- Cover1 (1).png',
      spec: '40 - 55 GSM',
      features: ['Durable', 'Smooth Finish', 'Label Friendly', 'Bulk Supply'],
      link: '/products/notebook-cover-jumbo-rolls',
    },
    {
      id: 3,
      name: 'Soap Stiffener Paper',
      category: 'Specialty Papers',
      image: '/images/products/Soap-Stiffener-Paper1 (1).png',
      spec: '70 - 90 GSM',
      features: ['High Thickness', 'Shape Retention', 'Moisture Protection', 'Custom GSM'],
      link: '/products/soap-stiffener-paper',
    },
    {
      id: 4,
      name: 'Food Wrap Paper',
      category: 'Food Packaging',
      image: '/images/products/food-wrap-papper (1).png',
      spec: '30 - 40 GSM',
      features: ['Grease Proof', 'Moisture Resistant', 'Food Grade', 'Print Ready'],
      link: '/products/food-wrap-paper',
    },
    {
      id: 5,
      name: 'Paper Cup Sleeves',
      category: 'Cup & Beverage',
      image: '/images/products/Paper-Cup-Sleeves.jpeg',
      spec: '35 - 45 GSM',
      features: ['Heat Insulation', 'Ergonomic Design', 'Premium Branding', 'Custom Print'],
      link: '/products/paper-cup-sleeves',
    },
    {
      id: 6,
      name: 'Ripple Paper Cup Sleeves',
      category: 'Cup & Beverage',
      image: '/images/products/Ripple-sleeve1 (1).png',
      spec: '60 - 80 GSM',
      features: ['Triple-Layered', 'Luxury Grip', 'Heat Barrier', 'Brand Ready'],
      link: '/products/ripple-paper-cup-sleeves',
    },
    {
      id: 7,
      name: 'Paper Plates',
      category: 'Food Packaging',
      image: '/images/products/Paper-Plates1 (1).png',
      spec: '80 - 120 GSM',
      features: ['Heavy-Duty', 'Eco-Friendly', 'Compostable', 'Food Safe'],
      link: '/products/paper-plates',
    },
  ],
}) => {
  const location = useLocation();

  const getCurrentProductName = () => {
    const path = location.pathname;
    if (path.includes('/pe-coated-rolls')) return 'PE Coated Rolls';
    if (path.includes('/food-wrap-paper')) return 'Food Wrap Paper';
    if (path.includes('/notebook-cover-jumbo-rolls')) return 'Notebook Cover Jumbo Rolls';
    if (path.includes('/soap-stiffener-paper')) return 'Soap Stiffener Paper';
    if (path.includes('/packaging')) return 'Packaging';
    if (path.includes('/pe-silver-coated-envelope-paper')) return 'PE Silver Coated Envelope Paper';
    if (path.includes('/sugar-sachets')) return 'Sugar Sachets';
    if (path.includes('/pouches-rolls')) return 'Pouches Rolls';
    if (path.includes('/cup-bottoms')) return 'Cup Bottoms';
    if (path.includes('/container-sleeves')) return 'Container Sleeves';
    if (path.includes('/box-tray-sleeves')) return 'Box Tray Sleeves';
    if (path.includes('/paper-bags')) return 'Paper Bags';
    if (path.includes('/paper-plates')) return 'Paper Plates';
    if (path.includes('/paper-carry-bags')) return 'Paper Carry Bags';
    if (path.includes('/paper-cup-sleeves')) return 'Paper Cup Sleeves';
    if (path.includes('/ripple-paper-cup-sleeves')) return 'Ripple Paper Cup Sleeves';
    if (path.includes('/bakery-solutions')) return 'Bakery Solutions';
    return null;
  };

  const currentProductName = getCurrentProductName();
  const filteredProducts = currentProductName
    ? products.filter((product) => product.name !== currentProductName)
    : products;

  const duplicatedProducts = [...filteredProducts, ...filteredProducts, ...filteredProducts];

  const titleParts = title.includes('Premium Range')
    ? { lead: 'Explore Our', accent: 'Premium Range' }
    : { lead: title, accent: null };

  const [scrollPaused, setScrollPaused] = useState(false);

  const pauseScroll = useCallback(() => setScrollPaused(true), []);
  const resumeScroll = useCallback(() => setScrollPaused(false), []);

  if (filteredProducts.length === 0) return null;

  return (
    <section className={styles.sectionContainer} aria-labelledby="related-products-heading">
      <div className={styles.bgDecor} aria-hidden="true">
        <div className={styles.orbOrange} />
        <div className={styles.orbGreen} />
        <div className={styles.meshGrid} />
        <div className={styles.topAccentLine} />
      </div>

      <div className={styles.wrapper}>
        <header className={styles.headerBlock}>
          <h2 id="related-products-heading" className={styles.sectionTitle}>
            {titleParts.accent ? (
              <>
                {titleParts.lead}{' '}
                <span className={styles.titleAccent}>{titleParts.accent}</span>
              </>
            ) : (
              titleParts.lead
            )}
          </h2>

          <p className={styles.sectionSubtitle}>{subtitle}</p>
        </header>

        <div className={styles.sliderShell}>
          <div
            className={styles.sliderContainer}
            onTouchStart={pauseScroll}
            onTouchEnd={resumeScroll}
            onTouchCancel={resumeScroll}
          >
            <div
              className={`${styles.sliderTrack} ${scrollPaused ? styles.sliderPaused : ''}`}
            >
              {duplicatedProducts.map((product, index) => (
                <Link
                  key={`${product.id}-${index}`}
                  to={product.link}
                  className={styles.productCard}
                  aria-label={`View ${product.name}`}
                >
                  <div className={styles.imageWrap}>
                    <img
                      src={product.image}
                      alt={product.name}
                      className={styles.productImg}
                      loading="lazy"
                    />
                    <span className={styles.specChip}>{product.spec}</span>
                  </div>

                  <div className={styles.cardBody}>
                    <span className={styles.categoryLabel}>{product.category}</span>
                    <h3 className={styles.productName}>{product.name}</h3>

                    <div className={styles.highlightGrid}>
                      {product.features.slice(0, MAX_FEATURES).map((feat) => (
                        <span key={feat} className={styles.highlightCell}>
                          {feat}
                        </span>
                      ))}
                    </div>

                    <span className={styles.viewMoreBtn}>
                      View More
                      <ArrowUpRight size={14} aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RelatedProducts;
